import React, { useState, useEffect } from 'react';
import { List, Typography } from 'antd';
import { CheckOutlined, CloseOutlined } from '@ant-design/icons';
import { StyledInput, SelectedItemsContainer, SelectedItemsWrap, DropdownContainer, SuggestionsDropdown, SectionTitle, SecondaryText, StyledTag, ListItemContent, ListItemSelected } from './styles';
import type { CategorySelectorProps } from './types';
import { PLACEHOLDERS, MESSAGES } from './constants';

const CategorySelector: React.FC<CategorySelectorProps> = ({
  categories,
  selectedCategories,
  onCategoryChange,
}) => {
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [query, setQuery] = useState('');
  const [filteredCategories, setFilteredCategories] = useState(categories);

  useEffect(() => {
    setFilteredCategories(categories);
  }, [categories]);

  const handleSearchCategories = (value: string) => {
    setQuery(value);
    if (value) {
      setFilteredCategories(
        categories.filter((item) =>
          item.category.toLowerCase().includes(value.toLowerCase())
        )
      );
    } else {
      setFilteredCategories(categories);
    }
  };

  const handleClearSearch = () => {
    setQuery('');
    setFilteredCategories(categories);
    setShowSuggestions(false);
  };

  const handleSelectCategory = (category: typeof categories[0]) => {
    if (!selectedCategories.some((c) => c.id === category.id)) {
      onCategoryChange([...selectedCategories, category]);
    }
    setShowSuggestions(false);
    setQuery('');
  };

  const handleRemoveCategory = (categoryId: string) => {
    const updated = selectedCategories.filter((c) => c.id !== categoryId);
    onCategoryChange(updated);
  };

  return (
    <div>
      <SectionTitle level={5}>Store Categories</SectionTitle>
      <DropdownContainer>
        <StyledInput
          placeholder={PLACEHOLDERS.SEARCH_CATEGORIES}
          value={query}
          onChange={(e) => handleSearchCategories(e.target.value)}
          onFocus={() => setShowSuggestions(true)}
          suffix={query ? <CloseOutlined onClick={handleClearSearch} style={{ cursor: 'pointer' }} /> : null}
        />

        {showSuggestions && filteredCategories.length > 0 && (
          <SuggestionsDropdown>
            <List
              dataSource={filteredCategories}
              renderItem={(category) => {
                const isSelected = selectedCategories.some((c) => c.id === category.id);
                return (
                  <ListItemSelected
                    $isSelected={isSelected}
                    onClick={() => handleSelectCategory(category)}
                  >
                    <ListItemContent style={{ width: '100%', justifyContent: 'space-between' }}>
                      <Typography.Text strong={isSelected} style={{ color: 'inherit' }}>
                        {category.category}
                      </Typography.Text>
                      {isSelected && <CheckOutlined style={{ color: '#52c41a' }} />}
                    </ListItemContent>
                  </ListItemSelected>
                );
              }}
            />
          </SuggestionsDropdown>
        )}
      </DropdownContainer>

      <SelectedItemsContainer>
        <Typography.Text strong>Selected Categories:</Typography.Text>
        {selectedCategories.length === 0 ? (
          <SecondaryText type="secondary">
            {MESSAGES.NO_CATEGORIES}
          </SecondaryText>
        ) : (
          <SelectedItemsWrap>
            {selectedCategories.map((category) => (
              <StyledTag
                key={category.id}
                closable
                onClose={() => handleRemoveCategory(category.id)}
                color="blue"
              >
                {category.category}
              </StyledTag>
            ))}
          </SelectedItemsWrap>
        )}
      </SelectedItemsContainer>
    </div>
  );
};

export default React.memo(CategorySelector);
